import React, { useState } from 'react';

import styled from 'styled-components';

import { motion } from 'framer-motion';

import Typography from '@mui/material/Typography';
import CardMedia from '@mui/material/CardMedia';
import CardActionArea from '@mui/material/CardActionArea';

import { fonts } from '../gatsby-theme-material-ui-top-layout/theme';

import ExternalLink from './ExternalLink';

import {
  extractPropertyLotSize,
  priceFormat,
  PROPY_SPACE_GREY_DARK,
  PROPY_LIGHT_BLUE,
  PROPY_FADED_GREY,
} from '../utils';

const StyledCardActionArea = styled(CardActionArea)`
    margin-bottom: ${props => props.theme.spacing(2)}!important;
    .MuiCardActionArea-focusHighlight {
        background: transparent;
    }
`;

const StatRow = styled.div`
  display: flex;
  justify-content: ${props => props.mobile ? 'center' : 'start'};
  align-items: center;
  opacity: ${props => props.hide ? 0 : 1};
`

const StatRowInner = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  margin-right: ${props => props.theme.spacing(1)};
`

const RoundedCardMedia = styled(motion(CardMedia))`
  border-radius: 8px;
`

const MultiPriceContainer = styled.div`
  display: flex;
  justify-content: ${props => props.mobile ? 'center' : 'start'};
  align-items: center;
  margin-top: ${props => props.theme.spacing(1)}!important;
  margin-bottom: ${props => props.theme.spacing(1)}!important;
`

const PriceTypographyDollars = styled(Typography)`
  font-family: ${fonts.robotoMono}!important;
  font-weight: 400!important;
  color: ${PROPY_SPACE_GREY_DARK}!important;
  font-size: 20px!important;
  letter-spacing: -0.07em!important;
  line-height: 20px!important;
`

const PriceTypographyBtc = styled(Typography)`
  font-family: ${fonts.robotoMono}!important;
  font-weight: 400!important;
  color: ${PROPY_LIGHT_BLUE}!important;
  font-size: 20px!important;
  letter-spacing: -0.07em!important;
  line-height: 20px!important;
`

const PriceSeparatorTypography = styled(Typography)`
  font-family: ${fonts.robotoMono}!important;
  font-weight: 400!important;
  color: ${PROPY_FADED_GREY}!important;
  font-size: 20px!important;
  letter-spacing: -0.07em!important;
  line-height: 20px!important;
  margin-right: ${props => props.theme.spacing(1)}!important;
  margin-left: ${props => props.theme.spacing(1)}!important;
`

const AddressTypography = styled(Typography)`
  font-family: ${fonts.spaceGrotesk}!important;
  font-weight: 400!important;
  color: ${PROPY_SPACE_GREY_DARK}!important;
  font-size: 14px!important;
  letter-spacing: 0.04em!important;
  line-height: 15px!important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: ${props => props.mobile ? 'center' : 'start'};
`

const StatTypography = styled(Typography).withConfig({
  shouldComponentUpdate: true,
})`
  font-family: ${fonts.spaceGrotesk}!important;
  font-weight: 400!important;
  color: ${PROPY_SPACE_GREY_DARK}!important;
  font-size: 14px!important;
  letter-spacing: 0.04em!important;
  line-height: 15px!important;
  text-align: ${props => props.mobile ? 'center' : 'start'};
`

const ChipContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`

const Chip = styled.div`
  color: ${props => props.config.textColor};
  background-color: ${props => props.config.backgroundColor};
  height: 34px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: ${props => props.theme.spacing(2)};
  padding-right: ${props => props.theme.spacing(2)};
`

const ListingCardUncontained = (props) => {

    const [hovered, setHovered] = useState(false);

    const {
      listing,
      btcPrice,
      isConsideredMobile,
    } = props;

    const imageVariants = {
      hover: {
        scale: 1.05,
        transition: {
          duration: 0.3,
          ease: "easeInOut"
        }
      },
      initial: {
        scale: 1,
        transition: {
          duration: 0.3,
          ease: "easeInOut"
        }
      }
    };

    return (
      <div>
        <ExternalLink fullWidth={true} href={`https://propy.com/property/${listing?.id}`}>
          <StyledCardActionArea 
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onTouchStart={() => setHovered(true)}
            onTouchEnd={() => setHovered(false)}
          >
            <div>
              <div style={{ overflow: 'hidden', borderRadius: '8px' }}>
                <RoundedCardMedia
                  component="img"
                  height="280"
                  image={listing?.imageUrls[0]}
                  alt="featured property media"
                  initial="initial"
                  animate={hovered ? "hover" : "initial"}
                  variants={imageVariants}
                />
                <ChipContainer>
                  {listing?.chips && listing.chips.map((chip, index) => 
                    <Chip key={`listing-card-uncontained-chip-${index}-${chip.label}`} config={chip}>
                      <Typography sx={{ fontFamily: fonts.spaceGrotesk, fontSize: '12px', letterSpacing: '0.04em', fontWeight: 400 }}>
                        {chip.label}
                      </Typography>
                    </Chip>
                  )}
                </ChipContainer>
              </div>
              <div>
                <MultiPriceContainer mobile={isConsideredMobile}>
                  <PriceTypographyDollars variant="h5" component="h5">
                    {priceFormat(`${listing?.price?.value}`, 0)}
                  </PriceTypographyDollars>
                  <PriceSeparatorTypography>
                    /
                  </PriceSeparatorTypography>
                  <PriceTypographyBtc variant="h5" component="h5">
                    {priceFormat(`${listing?.price?.value / btcPrice}`, 2, "BTC", false)}
                  </PriceTypographyBtc>
                </MultiPriceContainer>
                <AddressTypography mobile={isConsideredMobile} variant="h5" component="h5">
                  {listing?.address}
                </AddressTypography>
                <StatRow mobile={isConsideredMobile} hide={!(Number(listing?.bedrooms) || Number(listing?.bathrooms) || Number(listing?.size))}>
                  {listing?.bedrooms &&
                    <StatRowInner>
                      <StatTypography variant="subtitle1">
                        {listing?.bedrooms}BD
                      </StatTypography>
                    </StatRowInner>
                  }
                  {listing?.bathrooms &&
                    <StatRowInner>
                      <StatTypography variant="subtitle1">
                        {listing?.bathrooms}BA
                      </StatTypography>
                    </StatRowInner>
                  }
                  {listing?.size?.value > 0 &&
                    <StatRowInner>
                      <StatTypography variant="subtitle1">
                        {listing?.size && extractPropertyLotSize(listing?.size, 'value')}
                      </StatTypography>
                      <StatTypography variant="subtitle1">
                        {listing?.size && `${extractPropertyLotSize(listing?.size, 'label')}`}
                      </StatTypography>
                    </StatRowInner>
                  }
                </StatRow>
              </div>
            </div>
          </StyledCardActionArea>
        </ExternalLink>
      </div>
    )
};

export default ListingCardUncontained;